import React, { useCallback, useEffect, useLayoutEffect, useState, useRef } from 'react'
import { createPortal } from 'react-dom'
import { formatDateTime } from '@/utils/dateFormat'
import client from '@/api/client'
import {
  getInterviews,
  createInterview,
  updateInterview,
  cancelInterview,
  submitInterviewFeedback,
  getInterviewFeedback,
  getInterviewLinks,
  createInterviewLink,
  deleteInterviewLink,
  addInterviewAttendee,
  removeInterviewAttendee,
  InterviewCreateInput,
} from '@/api/tapResources'
import { getTapAssignees, TapAssignee } from '@/api/tapApplications'
import { TapInterview, TapInterviewAttendee, TapInterviewFeedback, TapInterviewResourceLink, TapScorecardTemplate, TapScorecardCompetency } from '@/types/index'
import { Modal } from '@/components/ui/Modal'
import { Plus, Link2, X, Users } from 'lucide-react'

// Feature #155 — Interviews tab. Supports multiple rounds (First / Second /
// Panel / Technical), each with their own schedule, prep, scorecard template
// and feedback capture.
interface InterviewsTabProps {
  applicationId: string
  // Feature #192: signal from parent to auto-open the "Schedule Interview" modal.
  autoOpenSchedule?: boolean
  onAutoOpenHandled?: () => void
}

const INTERVIEW_TYPES = ['First', 'Second', 'Panel', 'Technical']
const RECOMMENDATIONS = ['Proceed', 'Hold', 'Decline']

// WI #277: portal helper for typeahead dropdowns inside modals.
//
// Modal body uses ``overflow: auto`` so its content can scroll, which clips
// any ``position: absolute`` child that extends below the visible area —
// including our attendee-search dropdowns. The original bug report:
// "the typeahead dropdown is embedded within the modal popup, making it
// invisible unless you scroll down in the modal itself".
//
// Fix: render the dropdown panel into ``document.body`` via ``createPortal``
// so it escapes the modal's overflow constraint entirely, and position it
// with ``position: fixed`` using the anchor input's ``getBoundingClientRect``.
// The position is recomputed on scroll / resize so the panel tracks the
// input if the user scrolls the modal body.
//
// We deliberately keep this inline (and the panel inside the consumer's
// outside-click ref tree) so the existing outside-click + state hooks in
// InterviewsTab don't need to be reworked. The Z_INDEX sits above the modal
// (modal backdrop is 1000 — see Modal.tsx) so the dropdown overlays the
// modal content cleanly.
const PORTAL_DROPDOWN_Z_INDEX = 1100

function usePortalDropdownPosition(
  anchorRef: React.RefObject<HTMLElement>,
  open: boolean,
): { top: number; left: number; width: number } | null {
  const [pos, setPos] = useState<{ top: number; left: number; width: number } | null>(null)

  const recompute = useCallback(() => {
    if (!anchorRef.current) {
      setPos(null)
      return
    }
    const rect = anchorRef.current.getBoundingClientRect()
    setPos({
      top: rect.bottom + 4, // 4px gap matches the prior marginTop on the panel
      left: rect.left,
      width: rect.width,
    })
  }, [anchorRef])

  // useLayoutEffect so the position is set before the panel renders to
  // avoid a one-frame flash at (0, 0).
  useLayoutEffect(() => {
    if (!open) {
      setPos(null)
      return
    }
    recompute()
  }, [open, recompute])

  useEffect(() => {
    if (!open) return
    const handler = () => recompute()
    // Capture-phase listener so we catch scrolls inside the modal body too
    // (which doesn't bubble window scrolls).
    window.addEventListener('scroll', handler, true)
    window.addEventListener('resize', handler)
    return () => {
      window.removeEventListener('scroll', handler, true)
      window.removeEventListener('resize', handler)
    }
  }, [open, recompute])

  return pos
}

interface PortalDropdownProps {
  open: boolean
  anchorRef: React.RefObject<HTMLElement>
  /** Maximum panel height before its own internal scroll kicks in. */
  maxHeight?: number
  children: React.ReactNode
}

/**
 * Renders ``children`` in a panel anchored below ``anchorRef``, escaping any
 * scrollable ancestor via ``createPortal`` to ``document.body``.
 *
 * Consumers must still manage open/close state and outside-click detection
 * themselves — same shape as the prior absolutely-positioned dropdowns this
 * replaces, so the surrounding state machine doesn't change.
 */
const PortalDropdown: React.FC<PortalDropdownProps> = ({
  open,
  anchorRef,
  maxHeight = 200,
  children,
}) => {
  const pos = usePortalDropdownPosition(anchorRef, open)
  if (!open || !pos) return null
  return createPortal(
    <div
      // Stop mousedowns inside the panel from reaching the document-level
      // outside-click listener in the parent component. Without this, every
      // option click would propagate to document → close the dropdown →
      // unmount the option → click event fires on a dead node and the
      // selection is lost. Bubble-phase stopPropagation is enough because
      // the parent attaches via ``document.addEventListener('mousedown', …)``
      // without the capture flag.
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: pos.top,
        left: pos.left,
        width: pos.width,
        maxHeight,
        overflowY: 'auto',
        backgroundColor: 'var(--color-white)',
        border: '1px solid var(--color-light-grey)',
        borderRadius: '6px',
        zIndex: PORTAL_DROPDOWN_Z_INDEX,
        boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
      }}
    >
      {children}
    </div>,
    document.body,
  )
}

const cardStyle: React.CSSProperties = {
  backgroundColor: 'var(--color-white)',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '8px',
  padding: '20px',
  marginBottom: '16px',
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '8px 12px',
  border: '1px solid var(--color-light-grey)',
  borderRadius: '6px',
  fontSize: '14px',
  fontFamily: 'var(--font-family)',
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  fontWeight: 600,
  color: 'var(--color-dark-grey)',
  textTransform: 'uppercase',
  marginBottom: '4px',
}

const primaryBtn: React.CSSProperties = {
  padding: '8px 16px',
  backgroundColor: 'var(--color-magenta)',
  color: 'var(--color-white)',
  border: 'none',
  borderRadius: '6px',
  fontSize: '13px',
  fontWeight: 500,
  cursor: 'pointer',
  fontFamily: 'var(--font-family)',
}
const secondaryBtn: React.CSSProperties = {
  ...primaryBtn,
  backgroundColor: 'var(--color-white)',
  color: 'var(--color-black)',
  border: '1px solid var(--color-light-grey)',
}

const toDatetimeLocal = (iso?: string) => {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n: number) => n.toString().padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}
const fromDatetimeLocal = (local: string) => (local ? new Date(local).toISOString() : undefined)

// WI #275: traffic-light colours for the recommendation badge. Mirrors the
// score-badge palette so a quick visual scan ("green good / orange caution /
// red stop") works on both badges. The recommendation vocabulary is the
// constant RECOMMENDATIONS list below ('Proceed' / 'Hold' / 'Decline'); any
// non-matching value (legacy free-text data from before #177 introduced the
// select) falls back to a neutral grey badge.
const recommendationBadgeStyle = (recommendation: string): React.CSSProperties => {
  // Greys for the "unknown" / legacy free-text fallback.
  let bg = '#f3f4f6'
  let color = '#374151'
  const r = recommendation.toLowerCase()
  if (r === 'proceed') {
    bg = '#ecfdf5'
    color = '#047857'
  } else if (r === 'hold') {
    bg = '#fff7ed'
    color = '#c2410c'
  } else if (r === 'decline') {
    bg = '#fef2f2'
    color = '#b91c1c'
  }
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 700,
    backgroundColor: bg,
    color,
  }
}

// Feature #177: traffic-light banding for the interview score badge.
// 0–49 red, 50–79 orange, 80–100 green. Pulled out of the JSX so we
// can reuse the same banding in any future read-only views.
const scoreBadgeStyle = (score: number): React.CSSProperties => {
  let bg = '#fef2f2'
  let color = '#b91c1c'
  if (score >= 80) {
    bg = '#ecfdf5'
    color = '#047857'
  } else if (score >= 50) {
    bg = '#fff7ed'
    color = '#c2410c'
  }
  return {
    display: 'inline-block',
    padding: '3px 10px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: 700,
    backgroundColor: bg,
    color,
  }
}

const InterviewsTab: React.FC<InterviewsTabProps> = ({ applicationId, autoOpenSchedule, onAutoOpenHandled }) => {
  const [items, setItems] = useState<TapInterview[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [modalOpen, setModalOpen] = useState(false)
  const [editing, setEditing] = useState<TapInterview | null>(null)
  const [formType, setFormType] = useState('')
  const [formScheduledAt, setFormScheduledAt] = useState('')
  const [formDuration, setFormDuration] = useState('')
  const [formLocation, setFormLocation] = useState('')
  const [formPrep, setFormPrep] = useState('')
  // Feature #214: attendees staged in the create form. Only used when
  // creating (the edit flow continues to use the dedicated Attendees modal,
  // because edit happens at row-level after the interview already exists).
  const [formAttendeeUserIds, setFormAttendeeUserIds] = useState<string[]>([])
  const [formAttendeeSearch, setFormAttendeeSearch] = useState('')
  const [formAttendeeDropdownOpen, setFormAttendeeDropdownOpen] = useState(false)
  const formAttendeeDropdownRef = useRef<HTMLDivElement>(null)
  // WI #277: anchor ref for the portaled dropdown panel — the panel renders
  // into document.body and positions itself relative to this input.
  const formAttendeeInputRef = useRef<HTMLInputElement>(null)

  // Feature #177: scoring state. The Score Interview modal replaces the
  // legacy free-form "Add feedback" modal. We still capture recommendation +
  // free-form feedback text alongside the per-competency ratings so we don't
  // lose the qualitative bits.
  const [scoreFor, setScoreFor] = useState<TapInterview | null>(null)
  const [scoreTemplateId, setScoreTemplateId] = useState('')
  const [scoreCompetencies, setScoreCompetencies] = useState<TapScorecardCompetency[]>([])
  const [scoreCompetenciesLoading, setScoreCompetenciesLoading] = useState(false)
  const [scoreRatings, setScoreRatings] = useState<Record<string, number>>({})
  const [scoreRecommendation, setScoreRecommendation] = useState('')
  const [scoreFeedback, setScoreFeedback] = useState('')
  const [scoreEditingExisting, setScoreEditingExisting] = useState(false)
  // Templates eligible for scoring — only those whose competency weights sum
  // to 100 (#177 hard requirement).
  const [scoreTemplates, setScoreTemplates] = useState<TapScorecardTemplate[]>([])
  const [scoreTemplatesLoading, setScoreTemplatesLoading] = useState(false)
  // Per-interview feedback cache, populated on load. Used to swap the action
  // button between "Score interview" / "Change score" and to render the
  // colour-coded score badge on each interview row.
  const [feedbackByInterview, setFeedbackByInterview] = useState<Record<string, TapInterviewFeedback | null>>({})

  const [saving, setSaving] = useState(false)

  // Feature #200: resource links per interview.
  const [linksByInterview, setLinksByInterview] = useState<Record<string, TapInterviewResourceLink[]>>({})
  const [addLinkFor, setAddLinkFor] = useState<string | null>(null)
  const [linkLabel, setLinkLabel] = useState('')
  const [linkUrl, setLinkUrl] = useState('')

  // Feature #214: attendee management state.
  const MAX_ATTENDEES = 10
  const [attendeeModalFor, setAttendeeModalFor] = useState<TapInterview | null>(null)
  const [allUsers, setAllUsers] = useState<TapAssignee[]>([])
  const [userSearch, setUserSearch] = useState('')
  const [usersLoading, setUsersLoading] = useState(false)
  const [dropdownOpen, setDropdownOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)
  // WI #277: anchor ref for the Attendees-modal portaled dropdown panel.
  const attendeeInputRef = useRef<HTMLInputElement>(null)

  // WI #278: any active user can be an interview attendee (not just MANAGE_TAP
  // holders), so we pass include_all_active=true to widen the picker beyond
  // the default TAP-role gate. The dropdown is gated to MANAGE_TAP users by
  // virtue of being inside the TAP UI, so no privilege escalation — just a
  // wider candidate list.
  const loadUsers = async () => {
    if (allUsers.length > 0) return
    setUsersLoading(true)
    try {
      const users = await getTapAssignees(undefined, undefined, true)
      setAllUsers(users)
    } catch { /* silent */ }
    finally { setUsersLoading(false) }
  }

  const openAttendeeModal = (iv: TapInterview) => {
    setAttendeeModalFor(iv)
    setUserSearch('')
    setDropdownOpen(false)
    loadUsers()
  }

  const handleAddAttendee = async (userId: string) => {
    if (!attendeeModalFor) return
    const currentAttendees = attendeeModalFor.attendees || []
    if (currentAttendees.length >= MAX_ATTENDEES) {
      alert(`Maximum of ${MAX_ATTENDEES} attendees reached`)
      return
    }
    try {
      await addInterviewAttendee(applicationId, attendeeModalFor.id, userId)
      setUserSearch('')
      setDropdownOpen(false)
      await load()
      // Refresh the modal's interview data
      const refreshed = await getInterviews(applicationId)
      const updated = refreshed.find((iv) => iv.id === attendeeModalFor.id)
      if (updated) setAttendeeModalFor(updated)
    } catch {
      alert('Failed to add attendee')
    }
  }

  const handleRemoveAttendee = async (attendee: TapInterviewAttendee) => {
    if (!attendeeModalFor) return
    try {
      await removeInterviewAttendee(applicationId, attendeeModalFor.id, attendee.id)
      await load()
      const refreshed = await getInterviews(applicationId)
      const updated = refreshed.find((iv) => iv.id === attendeeModalFor.id)
      if (updated) setAttendeeModalFor(updated)
    } catch {
      alert('Failed to remove attendee')
    }
  }

  // Close dropdown on outside click
  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false)
      }
      // Feature #214: also close the create-form attendee dropdown.
      if (formAttendeeDropdownRef.current && !formAttendeeDropdownRef.current.contains(e.target as Node)) {
        setFormAttendeeDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClick)
    return () => document.removeEventListener('mousedown', handleClick)
  }, [])

  const loadLinks = async (interviewId: string) => {
    try {
      const links = await getInterviewLinks(applicationId, interviewId)
      setLinksByInterview((prev) => ({ ...prev, [interviewId]: links }))
    } catch { /* silent */ }
  }

  const handleAddLink = async () => {
    if (!addLinkFor || !linkLabel.trim() || !linkUrl.trim()) return
    setSaving(true)
    try {
      await createInterviewLink(applicationId, addLinkFor, linkLabel.trim(), linkUrl.trim())
      setAddLinkFor(null)
      setLinkLabel('')
      setLinkUrl('')
      await loadLinks(addLinkFor)
    } catch {
      alert('Failed to add link')
    } finally {
      setSaving(false)
    }
  }

  const handleRemoveLink = async (interviewId: string, linkId: string) => {
    try {
      await deleteInterviewLink(applicationId, interviewId, linkId)
      await loadLinks(interviewId)
    } catch {
      alert('Failed to remove link')
    }
  }

  const load = async () => {
    setLoading(true)
    setError(null)
    try {
      const interviews = await getInterviews(applicationId)
      setItems(interviews)
      // Feature #200: fetch resource links for each interview.
      for (const iv of interviews) {
        loadLinks(iv.id)
      }
      // Feature #177: fetch feedback (score) for each interview so the row
      // can show the colour-coded badge and the action button can pick the
      // right label ("Score interview" vs "Change score").
      const feedbackEntries = await Promise.all(
        interviews.map(async (iv) => {
          try {
            const fb = await getInterviewFeedback(applicationId, iv.id)
            return [iv.id, fb] as const
          } catch {
            return [iv.id, null] as const
          }
        }),
      )
      setFeedbackByInterview(Object.fromEntries(feedbackEntries))
    } catch {
      setError('Unable to load interviews')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applicationId])

  // Feature #192: auto-open the scheduling modal when arriving from a transition.
  useEffect(() => {
    if (autoOpenSchedule && !loading) {
      openCreate()
      onAutoOpenHandled?.()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoOpenSchedule, loading])

  const openCreate = () => {
    setEditing(null)
    setFormType('First')
    setFormScheduledAt('')
    setFormDuration('60')
    setFormLocation('')
    setFormPrep('')
    setFormAttendeeUserIds([])
    setFormAttendeeSearch('')
    setFormAttendeeDropdownOpen(false)
    // Eagerly load the user list so the picker has data when the user
    // opens the dropdown. Cached after first load.
    loadUsers()
    setModalOpen(true)
  }

  const openEdit = (iv: TapInterview) => {
    setEditing(iv)
    setFormType(iv.interviewType)
    setFormScheduledAt(toDatetimeLocal(iv.scheduledAt))
    setFormDuration(iv.durationMinutes?.toString() || '')
    setFormLocation(iv.locationOrLink || '')
    setFormPrep(iv.preparationNotes || '')
    setModalOpen(true)
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const payload: InterviewCreateInput = {
        interviewType: formType,
        scheduledAt: fromDatetimeLocal(formScheduledAt),
        durationMinutes: formDuration ? parseInt(formDuration, 10) : undefined,
        locationOrLink: formLocation || undefined,
        preparationNotes: formPrep || undefined,
        // Feature #214: only send attendees on create. Edit flow ignores
        // this field server-side (the schema is shared with update via
        // PATCH which uses InterviewUpdate, not InterviewCreate).
        attendeeUserIds: editing ? undefined : (formAttendeeUserIds.length ? formAttendeeUserIds : undefined),
      }
      if (editing) {
        await updateInterview(applicationId, editing.id, payload)
      } else {
        await createInterview(applicationId, payload)
      }
      setModalOpen(false)
      await load()
    } catch {
      alert('Failed to save interview')
    } finally {
      setSaving(false)
    }
  }

  const handleCancel = async (iv: TapInterview) => {
    if (!confirm('Cancel this interview?')) return
    try {
      await cancelInterview(applicationId, iv.id)
      await load()
    } catch {
      alert('Failed to cancel interview')
    }
  }

  // Feature #177: load competencies for a template the moment it's picked
  // in the Score Interview modal. We have to fetch them on-demand because
  // /tap/scorecard-templates returns just template metadata.
  const loadCompetenciesForTemplate = async (templateId: string) => {
    if (!templateId) {
      setScoreCompetencies([])
      return
    }
    setScoreCompetenciesLoading(true)
    try {
      const response = await client.get(`/tap/scorecard-templates/${templateId}/competencies`)
      const raw = Array.isArray(response.data) ? response.data : []
      const mapped: TapScorecardCompetency[] = raw.map((c: any) => ({
        id: c.id,
        templateId: c.template_id,
        name: c.name,
        description: c.description ?? undefined,
        weight: c.weight,
        sortOrder: c.sort_order,
        isActive: Boolean(c.is_active),
      }))
      setScoreCompetencies(mapped)
    } catch {
      setScoreCompetencies([])
    } finally {
      setScoreCompetenciesLoading(false)
    }
  }

  const openScore = async (iv: TapInterview) => {
    setScoreFor(iv)
    setScoreRecommendation('')
    setScoreFeedback('')
    setScoreRatings({})
    setScoreCompetencies([])

    // If templates haven't been loaded yet, pull the full list and filter to
    // those with valid weights (sum to 100). This is N+1 for now; if it ever
    // becomes a bottleneck, expose `total_weight` on the template list
    // response server-side.
    if (scoreTemplates.length === 0) {
      setScoreTemplatesLoading(true)
      try {
        const tplResp = await client.get('/tap/scorecard-templates')
        const rawList: any[] = tplResp.data?.items ?? []
        const ready: TapScorecardTemplate[] = []
        for (const raw of rawList) {
          // Pull competencies to check weights — done sequentially to keep
          // pressure on the backend predictable.
          const compResp = await client.get(`/tap/scorecard-templates/${raw.id}/competencies`)
          const comps: any[] = Array.isArray(compResp.data) ? compResp.data : []
          const total = comps.reduce((sum, c) => sum + (c.weight || 0), 0)
          if (total === 100) {
            ready.push({
              id: raw.id,
              name: raw.name,
              description: raw.description ?? undefined,
              isActive: Boolean(raw.is_active),
            })
          }
        }
        setScoreTemplates(ready)
      } catch {
        setScoreTemplates([])
      } finally {
        setScoreTemplatesLoading(false)
      }
    }

    // Prepopulate from existing feedback if there is one — this is the
    // "Change score" entry path. We also ensure the template the existing
    // feedback used is loaded into the picker even if its weights have since
    // drifted off 100 (a recruiter editing a previously-valid score).
    const existing = feedbackByInterview[iv.id]
    if (existing && existing.scorecardTemplateId) {
      setScoreEditingExisting(true)
      setScoreTemplateId(existing.scorecardTemplateId)
      setScoreRecommendation(existing.recommendation || '')
      setScoreFeedback(existing.feedback || '')
      const ratingsMap: Record<string, number> = {}
      for (const r of existing.responses ?? []) {
        ratingsMap[r.competencyId] = r.score
      }
      setScoreRatings(ratingsMap)
      await loadCompetenciesForTemplate(existing.scorecardTemplateId)
    } else {
      setScoreEditingExisting(false)
      setScoreTemplateId('')
    }
  }

  const handleScoreTemplateChange = async (newTemplateId: string) => {
    setScoreTemplateId(newTemplateId)
    // Reset ratings — different templates have different competencies, so
    // carrying ratings across a template change would map to the wrong rows.
    setScoreRatings({})
    if (newTemplateId) {
      await loadCompetenciesForTemplate(newTemplateId)
    } else {
      setScoreCompetencies([])
    }
  }

  // Live-computed total. Mirrors the backend formula exactly so the user
  // sees the same number the server will persist:
  //   total = sum over competencies of (rating / 5) * weight
  const liveScoreTotal = (() => {
    let total = 0
    for (const c of scoreCompetencies) {
      const rating = scoreRatings[c.id]
      if (typeof rating === 'number') total += (rating / 5) * c.weight
    }
    return Math.round(total)
  })()
  const allCompetenciesRated = scoreCompetencies.length > 0
    && scoreCompetencies.every((c) => typeof scoreRatings[c.id] === 'number')

  const handleScoreSave = async () => {
    if (!scoreFor || !scoreTemplateId || !allCompetenciesRated) return
    setSaving(true)
    try {
      const updated = await submitInterviewFeedback(applicationId, scoreFor.id, {
        recommendation: scoreRecommendation || undefined,
        feedback: scoreFeedback || undefined,
        scorecardTemplateId: scoreTemplateId,
        scorecardResponses: scoreCompetencies.map((c) => ({
          competencyId: c.id,
          score: scoreRatings[c.id]!,
        })),
      })
      // Update the cache so the row immediately reflects the new score.
      setFeedbackByInterview((prev) => ({ ...prev, [scoreFor.id]: updated }))
      setScoreFor(null)
      await load()
    } catch (err) {
      const detail =
        (err as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
      alert(typeof detail === 'string' ? `Failed to submit score: ${detail}` : 'Failed to submit score')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: 600, color: 'var(--color-black)' }}>Interviews</h3>
        <button onClick={openCreate} style={{ ...primaryBtn, display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
          <Plus size={14} /> Schedule interview
        </button>
      </div>

      {loading && <div style={{ color: 'var(--color-dark-grey)' }}>Loading…</div>}
      {error && <div style={{ color: 'var(--color-magenta)' }}>{error}</div>}

      {!loading && !error && items.length === 0 && (
        <div style={cardStyle}>
          <div style={{ color: 'var(--color-dark-grey)' }}>No interviews scheduled yet.</div>
        </div>
      )}

      {!loading && items.map((iv) => (
        <div key={iv.id} style={cardStyle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <div style={{ fontSize: '15px', fontWeight: 600 }}>
                {iv.interviewType} interview
                {iv.cancelledAt && (
                  <span style={{ marginLeft: '8px', fontSize: '12px', color: 'var(--color-magenta)' }}>Cancelled</span>
                )}
              </div>
              <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>
                {formatDateTime(iv.scheduledAt)}
                {iv.durationMinutes ? ` · ${iv.durationMinutes} min` : ''}
              </div>
              {iv.locationOrLink && (
                <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>
                  {iv.locationOrLink}
                </div>
              )}
              {iv.preparationNotes && (
                <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)', marginTop: '6px', whiteSpace: 'pre-wrap' }}>
                  <strong>Prep:</strong> {iv.preparationNotes}
                </div>
              )}
              {/* Feature #200: resource links */}
              {(linksByInterview[iv.id] || []).length > 0 && (
                <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {(linksByInterview[iv.id] || []).map((link) => (
                    <div key={link.id} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px' }}>
                      <Link2 size={12} style={{ color: 'var(--color-magenta)', flexShrink: 0 }} />
                      <a href={link.url} target="_blank" rel="noreferrer" style={{ color: 'var(--color-magenta)', textDecoration: 'none' }}>
                        {link.label}
                      </a>
                      {!iv.cancelledAt && (
                        <button
                          onClick={() => handleRemoveLink(iv.id, link.id)}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-dark-grey)' }}
                          title="Remove link"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {/* Feature #214: attendee names */}
              {(iv.attendees || []).length > 0 && (
                <div style={{ marginTop: '6px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                  <Users size={12} style={{ verticalAlign: 'middle', marginRight: '4px' }} />
                  {(iv.attendees || []).map((a) => a.displayName || 'Unknown').join(', ')}
                </div>
              )}
              {/* Feature #177: score badge — only when the interview has been
                  scored against a scorecard template. Shows the 0-100 total
                  with traffic-light banding plus the template name.
                  WI #275: also show the recommendation as a coloured badge
                  and any free-form notes underneath, so the qualitative
                  context the interviewer captured is visible without
                  re-opening the Score modal. */}
              {(() => {
                const fb = feedbackByInterview[iv.id]
                if (!fb || typeof fb.totalScore !== 'number') return null
                const tplName = scoreTemplates.find((t) => t.id === fb.scorecardTemplateId)?.name
                return (
                  <div style={{ marginTop: '8px', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={scoreBadgeStyle(fb.totalScore)}>{fb.totalScore} / 100</span>
                      {/* WI #275: recommendation badge sits next to the score
                          so a reviewer can see "85/100, Proceed" at a glance. */}
                      {fb.recommendation && (
                        <span style={recommendationBadgeStyle(fb.recommendation)}>
                          {fb.recommendation}
                        </span>
                      )}
                      {tplName && (
                        <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)' }}>
                          scored against <strong>{tplName}</strong>
                        </span>
                      )}
                    </div>
                    {/* WI #275: notes shown verbatim under the badges.
                        whitespace: pre-wrap preserves any line breaks the
                        interviewer typed; the wrapper limits width so very
                        long notes don't blow out the row. */}
                    {fb.feedback && (
                      <div
                        style={{
                          fontSize: '13px',
                          color: 'var(--color-dark-grey)',
                          whiteSpace: 'pre-wrap',
                          maxWidth: '720px',
                          lineHeight: '1.4',
                        }}
                      >
                        {fb.feedback}
                      </div>
                    )}
                  </div>
                )
              })()}
            </div>
            <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
              {!iv.cancelledAt && (
                <>
                  <button onClick={() => openEdit(iv)} style={secondaryBtn}>Reschedule</button>
                  {/* Feature #177: button label flips between "Score interview"
                      and "Change score" based on whether feedback exists. */}
                  <button onClick={() => openScore(iv)} style={secondaryBtn}>
                    {feedbackByInterview[iv.id] ? 'Change score' : 'Score interview'}
                  </button>
                  <button
                    onClick={() => openAttendeeModal(iv)}
                    style={secondaryBtn}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Users size={12} /> Attendees
                    </span>
                  </button>
                  <button
                    onClick={() => { setAddLinkFor(iv.id); setLinkLabel(''); setLinkUrl('') }}
                    style={secondaryBtn}
                  >
                    <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <Link2 size={12} /> Add link
                    </span>
                  </button>
                  <button
                    onClick={() => handleCancel(iv)}
                    style={{ ...secondaryBtn, color: 'var(--color-magenta)' }}
                  >
                    Cancel
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}

      {/* Schedule / reschedule modal */}
      <Modal
        isOpen={modalOpen}
        title={editing ? 'Reschedule interview' : 'Schedule interview'}
        onClose={() => setModalOpen(false)}
        isDirty={formType !== '' || formScheduledAt !== '' || formLocation !== '' || formPrep !== ''}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setModalOpen(false)} style={secondaryBtn}>Cancel</button>
            <button onClick={handleSave} style={primaryBtn} disabled={saving || !formType}>
              {saving ? 'Saving…' : editing ? 'Save' : 'Schedule'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Interview type</label>
            <select
              value={formType}
              onChange={(e) => setFormType(e.target.value)}
              style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
            >
              <option value="">Select…</option>
              {INTERVIEW_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Scheduled at</label>
            <input
              type="datetime-local"
              value={formScheduledAt}
              onChange={(e) => setFormScheduledAt(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Duration (minutes)</label>
            <input
              type="number"
              min={15}
              value={formDuration}
              onChange={(e) => setFormDuration(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Location or link</label>
            <input
              type="text"
              value={formLocation}
              onChange={(e) => setFormLocation(e.target.value)}
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>Preparation notes</label>
            <textarea
              value={formPrep}
              onChange={(e) => setFormPrep(e.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
            />
          </div>

          {/* Feature #214: assign TPL staff to attend the interview */}
          {!editing && (
            <div ref={formAttendeeDropdownRef} style={{ position: 'relative' }}>
              <label style={labelStyle}>Attendees ({formAttendeeUserIds.length}/{MAX_ATTENDEES})</label>
              {formAttendeeUserIds.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '6px' }}>
                  {formAttendeeUserIds.map((uid) => {
                    const u = allUsers.find((x) => x.id === uid)
                    return (
                      <span
                        key={uid}
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '6px',
                          padding: '4px 8px',
                          backgroundColor: 'var(--color-light-grey)',
                          borderRadius: '12px',
                          fontSize: '12px',
                        }}
                      >
                        {u?.displayName || uid}
                        <button
                          type="button"
                          onClick={() => setFormAttendeeUserIds((prev) => prev.filter((x) => x !== uid))}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-dark-grey)' }}
                          title="Remove attendee"
                        >
                          <X size={12} />
                        </button>
                      </span>
                    )
                  })}
                </div>
              )}
              {formAttendeeUserIds.length < MAX_ATTENDEES && (
                <>
                  <input
                    ref={formAttendeeInputRef}
                    type="text"
                    value={formAttendeeSearch}
                    onChange={(e) => { setFormAttendeeSearch(e.target.value); setFormAttendeeDropdownOpen(true) }}
                    onFocus={() => setFormAttendeeDropdownOpen(true)}
                    placeholder="Search by name…"
                    style={inputStyle}
                  />
                  {/* WI #277: dropdown panel rendered via portal so it
                      escapes the schedule-interview modal's overflow:auto
                      body and floats above all modal content. */}
                  <PortalDropdown open={formAttendeeDropdownOpen} anchorRef={formAttendeeInputRef}>
                    {usersLoading && (
                      <div style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>Loading users…</div>
                    )}
                    {!usersLoading && (() => {
                      const selected = new Set(formAttendeeUserIds)
                      const filtered = allUsers
                        .filter((u) => !selected.has(u.id))
                        .filter((u) => !formAttendeeSearch || u.displayName.toLowerCase().includes(formAttendeeSearch.toLowerCase()))
                        .slice(0, 20)
                      if (filtered.length === 0) {
                        return <div style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>No matching users</div>
                      }
                      return filtered.map((u) => (
                        <div
                          key={u.id}
                          onClick={() => {
                            setFormAttendeeUserIds((prev) => [...prev, u.id])
                            setFormAttendeeSearch('')
                            setFormAttendeeDropdownOpen(false)
                          }}
                          style={{
                            padding: '8px 12px',
                            fontSize: '13px',
                            cursor: 'pointer',
                            borderBottom: '1px solid var(--color-light-grey)',
                          }}
                          onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-light-grey)' }}
                          onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-white)' }}
                        >
                          {u.displayName}
                        </div>
                      ))
                    })()}
                  </PortalDropdown>
                </>
              )}
            </div>
          )}
        </div>
      </Modal>

      {/* Feature #200: Add resource link modal */}
      <Modal
        isOpen={Boolean(addLinkFor)}
        title="Add resource link"
        onClose={() => setAddLinkFor(null)}
        size="sm"
        isDirty={linkLabel !== '' || linkUrl !== ''}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
            <button onClick={() => setAddLinkFor(null)} style={secondaryBtn}>Cancel</button>
            <button onClick={handleAddLink} style={primaryBtn} disabled={saving || !linkLabel.trim() || !linkUrl.trim()}>
              {saving ? 'Saving…' : 'Add'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '12px' }}>
          <div>
            <label style={labelStyle}>Label</label>
            <input
              type="text"
              value={linkLabel}
              onChange={(e) => setLinkLabel(e.target.value)}
              placeholder="e.g. Position Description"
              style={inputStyle}
            />
          </div>
          <div>
            <label style={labelStyle}>URL</label>
            <input
              type="url"
              value={linkUrl}
              onChange={(e) => setLinkUrl(e.target.value)}
              placeholder="https://..."
              style={inputStyle}
            />
          </div>
        </div>
      </Modal>

      {/* Feature #214: Attendees modal */}
      <Modal
        isOpen={Boolean(attendeeModalFor)}
        title={`Attendees — ${attendeeModalFor?.interviewType ?? ''} interview`}
        onClose={() => setAttendeeModalFor(null)}
        size="sm"
        isDirty={false}
        footer={
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <button onClick={() => setAttendeeModalFor(null)} style={secondaryBtn}>Close</button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '12px' }}>
          {/* Current attendees */}
          {(attendeeModalFor?.attendees || []).length > 0 && (
            <div>
              <label style={labelStyle}>Current attendees ({(attendeeModalFor?.attendees || []).length}/{MAX_ATTENDEES})</label>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                {(attendeeModalFor?.attendees || []).map((a) => (
                  <div key={a.id} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '6px 10px', border: '1px solid var(--color-light-grey)', borderRadius: '6px', fontSize: '13px' }}>
                    <span>{a.displayName || 'Unknown'}</span>
                    <button
                      onClick={() => handleRemoveAttendee(a)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, color: 'var(--color-dark-grey)' }}
                      title="Remove attendee"
                    >
                      <X size={14} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add attendee picker */}
          {(attendeeModalFor?.attendees || []).length < MAX_ATTENDEES && (
            <div ref={dropdownRef} style={{ position: 'relative' }}>
              <label style={labelStyle}>Add attendee</label>
              <input
                ref={attendeeInputRef}
                type="text"
                value={userSearch}
                onChange={(e) => { setUserSearch(e.target.value); setDropdownOpen(true) }}
                onFocus={() => setDropdownOpen(true)}
                placeholder="Search by name…"
                style={inputStyle}
              />
              {/* WI #277: portal so the dropdown panel escapes the Attendees
                  modal's overflow:auto body. Previously the typeahead list
                  was clipped to whatever fit inside the visible modal area
                  and effectively invisible unless the user scrolled. */}
              <PortalDropdown open={dropdownOpen} anchorRef={attendeeInputRef}>
                {usersLoading && (
                  <div style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>Loading users…</div>
                )}
                {!usersLoading && (() => {
                  const currentIds = new Set((attendeeModalFor?.attendees || []).map((a) => a.userId))
                  const filtered = allUsers
                    .filter((u) => !currentIds.has(u.id))
                    .filter((u) => !userSearch || u.displayName.toLowerCase().includes(userSearch.toLowerCase()))
                    .slice(0, 20)
                  if (filtered.length === 0) {
                    return <div style={{ padding: '8px 12px', fontSize: '13px', color: 'var(--color-dark-grey)' }}>No matching users</div>
                  }
                  return filtered.map((u) => (
                    <div
                      key={u.id}
                      onClick={() => handleAddAttendee(u.id)}
                      style={{
                        padding: '8px 12px',
                        fontSize: '13px',
                        cursor: 'pointer',
                        borderBottom: '1px solid var(--color-light-grey)',
                      }}
                      onMouseEnter={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-light-grey)' }}
                      onMouseLeave={(e) => { (e.currentTarget as HTMLDivElement).style.backgroundColor = 'var(--color-white)' }}
                    >
                      {u.displayName}
                    </div>
                  ))
                })()}
              </PortalDropdown>
            </div>
          )}
        </div>
      </Modal>

      {/* Feature #177: Score Interview modal. Replaces the legacy free-form
          "Add feedback" modal. The user picks a Scorecard Template, rates
          each competency 1-5, and submits — the server computes the 0-100
          weighted total and persists per-competency responses. The same modal
          handles both first-time scoring and "Change score" via the
          scoreEditingExisting flag. */}
      <Modal
        isOpen={Boolean(scoreFor)}
        title={scoreEditingExisting ? 'Change score' : 'Score interview'}
        size="lg"
        onClose={() => setScoreFor(null)}
        isDirty={scoreTemplateId !== '' || Object.keys(scoreRatings).length > 0}
        footer={
          <div style={{ display: 'flex', gap: '8px', justifyContent: 'flex-end', alignItems: 'center', width: '100%' }}>
            {/* Live total preview — useful especially in "Change score" mode. */}
            {scoreCompetencies.length > 0 && (
              <div style={{ marginRight: 'auto', fontSize: '13px', color: 'var(--color-dark-grey)' }}>
                Score: <span style={scoreBadgeStyle(liveScoreTotal)}>{liveScoreTotal} / 100</span>
                {!allCompetenciesRated && (
                  <span style={{ marginLeft: '8px', fontSize: '12px' }}>(rate every competency to submit)</span>
                )}
              </div>
            )}
            <button onClick={() => setScoreFor(null)} style={secondaryBtn}>Cancel</button>
            <button
              onClick={handleScoreSave}
              style={primaryBtn}
              disabled={saving || !scoreTemplateId || !allCompetenciesRated}
            >
              {saving ? 'Saving…' : scoreEditingExisting ? 'Save changes' : 'Submit score'}
            </button>
          </div>
        }
      >
        <div style={{ display: 'grid', gap: '16px' }}>
          <div>
            <label style={labelStyle}>Scorecard template</label>
            {scoreTemplatesLoading ? (
              <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>Loading templates…</div>
            ) : (
              <select
                value={scoreTemplateId}
                onChange={(e) => handleScoreTemplateChange(e.target.value)}
                style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
              >
                <option value="">Select a template…</option>
                {scoreTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            )}
            {!scoreTemplatesLoading && scoreTemplates.length === 0 && (
              <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '6px' }}>
                No scorecard templates with weights summing to 100 are available. Create or fix a template under TAP Settings → Scorecard Templates.
              </div>
            )}
          </div>

          {/* Competency rating rows. 1-5 radios so the rating is unambiguous
              (not a slider, not a number input — easier to scan and matches
              the interview-panel mental model). */}
          {scoreCompetenciesLoading && (
            <div style={{ fontSize: '13px', color: 'var(--color-dark-grey)' }}>Loading competencies…</div>
          )}
          {!scoreCompetenciesLoading && scoreCompetencies.length > 0 && (
            <div style={{ display: 'grid', gap: '10px' }}>
              {scoreCompetencies.map((c) => {
                const rating = scoreRatings[c.id]
                return (
                  <div
                    key={c.id}
                    style={{
                      border: '1px solid var(--color-light-grey)',
                      borderRadius: '8px',
                      padding: '12px 14px',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px' }}>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: 600 }}>{c.name}</div>
                        {c.description && (
                          <div style={{ fontSize: '12px', color: 'var(--color-dark-grey)', marginTop: '2px' }}>{c.description}</div>
                        )}
                      </div>
                      <span style={{ fontSize: '12px', color: 'var(--color-dark-grey)', whiteSpace: 'nowrap' }}>
                        weight {c.weight}
                      </span>
                    </div>
                    <div style={{ display: 'flex', gap: '8px', marginTop: '10px' }}>
                      {[1, 2, 3, 4, 5].map((n) => (
                        <label
                          key={n}
                          style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            width: '36px',
                            height: '36px',
                            border: `1px solid ${rating === n ? 'var(--color-magenta)' : 'var(--color-light-grey)'}`,
                            backgroundColor: rating === n ? '#fdf2f8' : 'var(--color-white)',
                            color: rating === n ? 'var(--color-magenta)' : 'var(--color-black)',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontWeight: rating === n ? 700 : 500,
                            fontSize: '14px',
                          }}
                        >
                          <input
                            type="radio"
                            name={`rating-${c.id}`}
                            value={n}
                            checked={rating === n}
                            onChange={() => setScoreRatings((prev) => ({ ...prev, [c.id]: n }))}
                            style={{ display: 'none' }}
                          />
                          {n}
                        </label>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Recommendation + free-form notes are kept from the legacy flow.
              They're optional and orthogonal to the numeric score, but valuable
              qualitative context for the audit trail. */}
          <div>
            <label style={labelStyle}>Recommendation (optional)</label>
            <select
              value={scoreRecommendation}
              onChange={(e) => setScoreRecommendation(e.target.value)}
              style={{ ...inputStyle, backgroundColor: 'var(--color-white)', cursor: 'pointer' }}
            >
              <option value="">—</option>
              {RECOMMENDATIONS.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label style={labelStyle}>Notes (optional)</label>
            <textarea
              value={scoreFeedback}
              onChange={(e) => setScoreFeedback(e.target.value)}
              rows={4}
              style={{ ...inputStyle, resize: 'vertical' }}
              placeholder="Any qualitative feedback to capture alongside the score…"
            />
          </div>
        </div>
      </Modal>
    </div>
  )
}

export default InterviewsTab
